import { useEffect, useState } from 'react'

export interface VersionEntry {
  v: number
  timestamp: string
  rows: number
  source: string
  sha256: string
}

export function TimeMachineModal({ handle, slug, currentVersion, onClose }: {
  handle: string; slug: string; currentVersion: number; onClose: () => void
}) {
  const [versions, setVersions] = useState<VersionEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState(0)

  useEffect(() => {
    fetch(`/${handle}/${slug}/versions`, { credentials: 'include' })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json() as Promise<{ versions: VersionEntry[] }>
      })
      .then(d => setVersions(d.versions ?? []))
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load versions'))
  }, [handle, slug])

  const cur = versions && versions[selected]

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.74)', backdropFilter: 'blur(8px)',
      zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32,
    }}>
      <div className="card" onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 1080, height: 640, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div className="row" style={{ padding: '16px 22px', borderBottom: '1px solid var(--line-soft)', justifyContent: 'space-between' }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 4 }}>time machine</div>
            <div className="h3">{handle}/{slug}<span className="muted-2 mono" style={{ marginLeft: 12, fontSize: 12 }}>· current v{currentVersion}</span></div>
          </div>
          <span style={{ cursor: 'pointer', color: 'var(--fg-3)', fontSize: 22 }} onClick={onClose}>×</span>
        </div>

        {error && (
          <div style={{ padding: 24 }}>
            <div className="mono" style={{ color: 'var(--err)', fontSize: 13 }}>{error}</div>
          </div>
        )}

        {!error && !versions && (
          <div style={{ padding: 24, color: 'var(--fg-3)', fontSize: 13 }}>Loading versions…</div>
        )}

        {versions && versions.length === 0 && (
          <div style={{ padding: 24, color: 'var(--fg-3)', fontSize: 13 }}>No versions yet.</div>
        )}

        {versions && versions.length > 0 && cur && (
          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '320px 1fr', minHeight: 0 }}>
            <div style={{ borderRight: '1px solid var(--line-soft)', overflow: 'auto' }}>
              {versions.map((v, i) => (
                <div key={v.v} onClick={() => setSelected(i)}
                  style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid var(--line-soft)',
                    background: selected === i ? 'var(--accent-soft)' : 'transparent',
                    borderLeft: selected === i ? '2px solid var(--accent)' : '2px solid transparent',
                    cursor: 'pointer',
                  }}>
                  <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
                    <span className="mono" style={{ fontSize: 13, color: selected === i ? 'var(--accent)' : 'var(--fg)', fontWeight: 600 }}>v{v.v}</span>
                    <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>{v.source}</span>
                  </div>
                  <div className="mono" style={{ fontSize: 11, color: 'var(--fg-2)', marginBottom: 4 }}>{new Date(v.timestamp).toLocaleString()}</div>
                  <div className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>{v.rows.toLocaleString()} rows</div>
                </div>
              ))}
            </div>

            <div style={{ padding: 24, overflow: 'auto' }}>
              <div className="row" style={{ marginBottom: 18, gap: 12 }}>
                <span className="mono" style={{ fontSize: 24, fontWeight: 700, color: 'var(--accent)' }}>v{cur.v}</span>
                <span className="mono muted" style={{ fontSize: 13 }}>· {new Date(cur.timestamp).toLocaleString()}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 22 }}>
                {[
                  { l: 'rows', v: cur.rows.toLocaleString() },
                  { l: 'source', v: cur.source },
                  { l: 'sha256', v: cur.sha256 ? cur.sha256.slice(0, 10) + '…' : '—' },
                  { l: 'is current', v: cur.v === currentVersion ? 'yes' : 'no', c: cur.v === currentVersion ? 'var(--lime)' : 'var(--fg-2)' },
                ].map(s => (
                  <div key={s.l} style={{ padding: 12, background: 'var(--bg)', borderRadius: 6, border: '1px solid var(--line-soft)' }}>
                    <div className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{s.l}</div>
                    <div className="mono" style={{ fontSize: 14, fontWeight: 500, color: s.c || 'var(--fg)' }}>{s.v}</div>
                  </div>
                ))}
              </div>
              <div className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>storage ref</div>
              <code className="mono" style={{ fontSize: 11, padding: '8px 12px', background: 'var(--bg)', border: '1px solid var(--line-soft)', borderRadius: 5, color: 'var(--accent)', display: 'block' }}>
                r2://instadash/grids/{handle}/{slug}/v{cur.v}.jsonl
              </code>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
